import { apiErrorMessage } from '@agos/api-client'
import {
  getGetSettingsIntegrationsQueryKey,
  useGetSettingsIntegrations,
  usePutSettingsIntegrations,
} from '@agos/api-client/platform'
import { getGetHealthCircuitBreakersQueryKey, useGetHealthCircuitBreakers } from '@agos/api-client/health'
import type { IntegrationSettings } from '@agos/api-client/model'
import { T, formatCount, formatDateTime, tt, useAuth, useFranchiseModules } from '@agos/shared'
import { PageHeader, Spinner, cn, useToast, BUTTON_PRIMARY} from '@agos/ui'
import { useQueryClient } from '@tanstack/react-query'
import { AlertTriangle, Info, RefreshCw, Save, ShieldAlert, Truck } from 'lucide-react'
import { useState } from 'react'

import { mayWrite, readOnlyReason } from '../write-access'
import {
  PLATFORMS,
  breakerLabel,
  breakerNote,
  breakerStateLabel,
  breakersOf,
  formOf,
  isLabelledBreaker,
  lastSyncLabel,
  moduleKey,
  payloadOf,
  platformName,
  stateOf,
  statusOf,
  unimplementedNote,
  venueProblem,
  type IntegrationsForm,
  type Platform,
  type PlatformForm,
} from './integration-settings'

/**
 * `/settings-integrations` — Wolt and Bolt Food, and the breakers of four services that are neither.
 *
 * **FB-148.** The integration does not exist. `wolt` appears in two files across both backends: the read and
 * write paths of this screen. No menu push, no order ingestion, no webhook, no scheduler — and no order in
 * the 10 491 on record came from either platform. Twelve switches whose only effect is the row they are
 * stored in, and the page now says so instead of drawing a configuration.
 *
 * **FB-149.** The breaker panel lists `resend`, `tatrapay-api`, `tatra-banka-token` and `tatra-banka-api` —
 * payments, a bank and e-mail, on a page about food delivery. Legacy's label table names two `fiskalpro-*`
 * breakers the endpoint never returns and leaves two it does as raw keys, passed through `tt()`. Both are
 * corrected and the panel says whose services these are.
 *
 * **What legacy gets right, kept unchanged:** the API key is never fetched — the response carries only
 * `hasWoltApiKey` — and the field is sent only when somebody types in it. That is the pattern FB-138,
 * FB-143 and FB-132 had to be argued into everywhere else.
 *
 * Ported from legacy `pages/settings-integrations.tsx` (348 lines).
 */

export function SettingsIntegrationsPage() {
  const settings = useGetSettingsIntegrations()

  if (settings.isLoading) {
    return (
      <div className="flex justify-center py-16">
        <Spinner />
      </div>
    )
  }

  return <IntegrationsEditor settings={settings.data} />
}

const FIELD =
  'px-3 py-2 rounded-lg bg-background border border-border text-sm outline-none focus:ring-2 focus:ring-primary/20 w-56 disabled:opacity-60'

function IntegrationsEditor({ settings }: { settings: IntegrationSettings | undefined }) {
  const { toast } = useToast()
  const { user } = useAuth()
  const { isModuleEnabled } = useFranchiseModules()
  const queryClient = useQueryClient()

  const [form, setForm] = useState<IntegrationsForm>(() => formOf(settings))

  const writable = mayWrite('integrations', user?.systemRole)
  const readOnly = readOnlyReason('integrations', user?.systemRole)

  const save = usePutSettingsIntegrations({
    mutation: {
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: getGetSettingsIntegrationsQueryKey() })
        // The key fields empty themselves: what is stored is now stored, and leaving the typed value on
        // screen would invite a second save of a credential that is already in place.
        // Built from PLATFORMS, not listed. Listed, a third platform kept its typed key on screen
        // after a successful save - which invites saving a credential that is already stored.
        setForm((current) =>
          Object.fromEntries(
            PLATFORMS.map((platform) => [platform, { ...current[platform], apiKey: '' }]),
          ) as typeof current,
        )
        toast({ title: tt('Nastavenia prepojení uložené') })
      },
      onError: (caught) =>
        toast({
          variant: 'destructive',
          title: tt('Uloženie zlyhalo'),
          description: apiErrorMessage(caught, tt('Na serveri sa nič nezmenilo.')),
        }),
    },
  })

  const set = (platform: Platform, patch: Partial<PlatformForm>) =>
    setForm((current) => ({ ...current, [platform]: { ...current[platform], ...patch } }))

  const visible = PLATFORMS.filter((platform) => isModuleEnabled(moduleKey(platform)))

  return (
    <div className="space-y-5">
      <PageHeader
        // §9.275 — the h1 matches the sidebar, and both stop naming only half of what is here.
        title={tt('Rozvozové platformy')}
        description={tt('Údaje pre doručovacie platformy')}
        icon={<Truck className="w-5 h-5" />}
      />

      {readOnly !== null && (
        <p className="text-[11px] text-amber-500" data-read-only>
          {readOnly}
        </p>
      )}

      {/* FB-148. The page's whole premise, said once and first. */}
      <div
        className="rounded-2xl border border-amber-500/40 bg-amber-500/5 px-4 py-3 flex items-start gap-3"
        data-not-implemented
      >
        <AlertTriangle className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
        <div className="min-w-0">
          <p className="text-sm font-semibold text-amber-500">
            <T>Prepojenie zatiaľ nie je naprogramované</T>
          </p>
          <p className="text-xs text-muted-foreground mt-0.5">{unimplementedNote()}</p>
        </div>
      </div>

      <Breakers />

      {visible.map((platform) => {
        const one = form[platform]
        const state = stateOf(settings, platform)
        const problem = venueProblem(one)
        return (
          <div
            key={platform}
            className="bg-card border border-border rounded-2xl overflow-hidden"
            data-platform={platform}
            data-status={statusOf(one, state)}
          >
            <div className="px-5 py-4 border-b border-border flex items-center gap-2">
              <Truck className="w-4 h-4 text-primary" />
              <h2 className="text-xs font-bold text-muted-foreground uppercase tracking-wider">
                {platformName(platform)}
              </h2>
            </div>
            <div className="divide-y divide-border">
              <Row
                label={tt('API kľúč')}
                desc={
                  state.hasApiKey
                    ? tt('Kľúč je uložený. Vypĺňa sa len keď ho chcete zmeniť.')
                    : tt('Kľúč nie je uložený.')
                }
              >
                <input
                  type="password"
                  aria-label={`${tt('API kľúč')} - ${platformName(platform)}`}
                  className={FIELD}
                  autoComplete="off"
                  disabled={!writable}
                  placeholder={state.hasApiKey ? tt('uložený - nechajte prázdne') : tt('vložte kľúč')}
                  value={one.apiKey}
                  onChange={(event) => set(platform, { apiKey: event.target.value })}
                />
                <p className="text-[11px] text-muted-foreground" data-has-key={state.hasApiKey ? 'yes' : 'no'}>
                  {state.hasApiKey ? <T>Uložený kľúč sa odtiaľto nezobrazuje.</T> : <T>Bez kľúča.</T>}
                </p>
              </Row>
              <Row
                label={tt('ID prevádzky')}
                desc={`${tt('Identifikátor prevádzky na platforme')} ${platformName(platform)}`}
              >
                <input
                  aria-label={`${tt('ID prevádzky')} - ${platformName(platform)}`}
                  className={cn(FIELD, problem !== null && 'border-amber-500')}
                  disabled={!writable}
                  placeholder="venue-12345"
                  value={one.venueId}
                  onChange={(event) => set(platform, { venueId: event.target.value })}
                />
                {problem !== null && (
                  <p className="text-[11px] text-amber-500" data-venue-problem={platform}>
                    <T>Prepojenie je zapnuté, ale ID prevádzky chýba.</T>
                  </p>
                )}
              </Row>
              <Toggle
                label={tt('Aktívne prepojenie')}
                desc={tt('Poznámka - nič ju nečíta.')}
                value={one.active}
                disabled={!writable}
                onToggle={() => set(platform, { active: !one.active })}
                testId={`${platform}-active`}
              />
              <Toggle
                label={tt('Automatická synchronizácia menu')}
                desc={tt('Poznámka - žiadna úloha menu neposiela.')}
                value={one.menuSync}
                disabled={!writable}
                onToggle={() => set(platform, { menuSync: !one.menuSync })}
                testId={`${platform}-menu-sync`}
              />
              <Toggle
                label={tt('Prijímanie objednávok cez platformu')}
                desc={tt('Poznámka - žiadny koncový bod objednávky z platformy neprijíma.')}
                value={one.acceptOrders}
                disabled={!writable}
                onToggle={() => set(platform, { acceptOrders: !one.acceptOrders })}
                testId={`${platform}-accept-orders`}
              />
              <Row
                label={tt('Posledná synchronizácia')}
                desc={tt('Čo server hlási - a nesynchronizovalo sa nikdy.')}
              >
                <p className="text-sm text-muted-foreground" data-last-sync={platform}>
                  {lastSyncLabel(state)}
                </p>
              </Row>
            </div>
          </div>
        )
      })}

      {visible.length === 0 && (
        <p className="text-sm text-muted-foreground" data-no-platform>
          <T>Ani jedna doručovacia platforma nie je v balíku tejto franšízy.</T>
        </p>
      )}

      <div className="flex items-center gap-3">
        <button
          type="button"
          data-save
          disabled={!writable || save.isPending}
          onClick={() => save.mutate({ data: { ...payloadOf(form) } })}
          className={cn('flex items-center gap-2 px-5 py-2.5 rounded-xl', BUTTON_PRIMARY, 'text-sm')}
        >
          {save.isPending ? <Spinner /> : <Save className="w-4 h-4" />} <T>Uložiť nastavenia</T>
        </button>
      </div>
    </div>
  )
}

/** FB-149. Four services, none of them on this page's subject. */
function Breakers() {
  const query = useGetHealthCircuitBreakers()
  const queryClient = useQueryClient()
  const breakers = breakersOf(query.data)
  const note = breakerNote(breakers)

  return (
    <div className="bg-card border border-border rounded-2xl overflow-hidden" data-breakers={breakers.length}>
      <div className="px-5 py-4 border-b border-border flex items-center gap-2">
        <ShieldAlert className="w-4 h-4 text-muted-foreground" />
        <h2 className="text-xs font-bold text-muted-foreground uppercase tracking-wider flex-1">
          {/* §9.275 - was *Stav externých služieb*, inside a sidebar section already called *Externé
            * služby*. This panel's subject is the four breakers of services this system talks to,
            * which is what it says now. */}
          <T>Stav pripojených služieb</T>
        </h2>
        <button
          type="button"
          data-refresh-breakers
          disabled={query.isFetching}
          onClick={() =>
            void queryClient.invalidateQueries({ queryKey: getGetHealthCircuitBreakersQueryKey() })
          }
          className="flex items-center gap-1.5 text-xs text-muted-foreground disabled:opacity-50"
        >
          <RefreshCw className={cn('w-3.5 h-3.5', query.isFetching && 'animate-spin')} /> <T>Obnoviť</T>
        </button>
      </div>

      {note !== '' && (
        <p className="px-5 py-3 text-xs text-muted-foreground flex items-start gap-2" data-breaker-note>
          <Info className="w-3.5 h-3.5 shrink-0 mt-0.5" />
          {note}
        </p>
      )}

      {breakers.length > 0 && (
        <div className="divide-y divide-border">
          {breakers.map((breaker) => (
            <div
              key={breaker.name}
              className="flex items-center justify-between px-5 py-3 gap-4"
              data-breaker={breaker.name}
              data-breaker-state={breaker.state}
            >
              <div className="min-w-0 flex-1">
                {/* A service name is data; legacy passed the raw key through `tt()`. */}
                <p
                  className="text-sm font-medium truncate"
                  data-breaker-label={isLabelledBreaker(breaker.name) ? 'labelled' : 'raw'}
                >
                  {breakerLabel(breaker.name)}
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  <T>Zlyhania</T>: {formatCount(breaker.failureCount)}
                  {breaker.lastFailure !== null && ` · ${formatDateTime(breaker.lastFailure)}`}
                  {breaker.nextRetryAt !== null &&
                    breaker.state === 'OPEN' &&
                    ` · ${tt('ďalší pokus')} ${formatDateTime(breaker.nextRetryAt)}`}
                </p>
              </div>
              <span
                className={cn(
                  'text-xs font-bold px-2.5 py-1 rounded-full shrink-0',
                  breaker.state === 'CLOSED' && 'bg-emerald-500/15 text-emerald-500',
                  breaker.state === 'OPEN' && 'bg-red-500/15 text-red-500',
                  breaker.state === 'HALF_OPEN' && 'bg-amber-500/15 text-amber-500',
                )}
              >
                {breakerStateLabel(breaker.state)}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function Row({
  label,
  desc,
  children,
}: {
  label: string
  desc: string
  children: React.ReactNode
}) {
  return (
    <div className="flex items-start justify-between px-5 py-4 gap-4">
      <div className="flex-1 pt-1 min-w-0">
        <p className="font-semibold text-foreground text-sm">{label}</p>
        <p className="text-xs text-muted-foreground mt-0.5">{desc}</p>
      </div>
      <div className="shrink-0 space-y-1">{children}</div>
    </div>
  )
}

function Toggle({
  label,
  desc,
  value,
  disabled,
  onToggle,
  testId,
}: {
  label: string
  desc: string
  value: boolean
  disabled: boolean
  onToggle: () => void
  testId: string
}) {
  return (
    <div className="flex items-start justify-between px-5 py-4 gap-4" data-toggle={testId}>
      <div className="flex-1 min-w-0">
        <p className="font-semibold text-foreground text-sm">{label}</p>
        <p className="text-xs text-muted-foreground mt-0.5">{desc}</p>
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={value}
        aria-label={label}
        disabled={disabled}
        data-toggle-state={value ? 'on' : 'off'}
        onClick={onToggle}
        className={cn(
          'w-11 h-6 rounded-full relative shrink-0 transition-colors disabled:opacity-50',
          value ? 'bg-primary' : 'bg-muted',
        )}
      >
        <span
          className={cn(
            'absolute top-0.5 w-5 h-5 rounded-full bg-background transition-all',
            value ? 'left-[22px]' : 'left-0.5',
          )}
        />
      </button>
    </div>
  )
}
